import type { AppNotification, ToastContent } from './send-notifications';

import { safeLocalStorage } from '@/lib/safe-storage';
import { CANONICAL_INBOX_PATH } from '@/utils/app-paths';

/** More missed notifications than this show as one toast that sums them up. */
const MAX_CATCH_UP_TOASTS = 3;

/**
 * More than the server logs, so every logged notification the desktop app showed stays recorded.
 */
const MAX_RECORDED_IDS = 50;

/**
 * Notifications this window is showing or showed, because a live event and a catch-up can both
 * bring one.
 */
const claimedIds = new Set<string>();

type ShowToast = (toast: ToastContent) => Promise<boolean>;

function shownIdsKey(userId: string): string {
  return `mrge:desktop-notifications-shown:${userId}`;
}

/**
 * The ids the desktop app showed a person, oldest first, kept across restarts; null before its
 * first catch-up or when what is stored is not a list. Entries that are not ids are dropped.
 */
function readShownIds(userId: string): string[] | null {
  try {
    const stored: unknown = JSON.parse(safeLocalStorage.getItem(shownIdsKey(userId)) ?? 'null');
    return Array.isArray(stored)
      ? stored.filter((id): id is string => typeof id === 'string')
      : null;
  } catch {
    return null;
  }
}

function recordShown(userId: string, ids: string[]): void {
  const previous = readShownIds(userId) ?? [];
  safeLocalStorage.setItem(
    shownIdsKey(userId),
    JSON.stringify(
      [...previous.filter((id) => !ids.includes(id)), ...ids].slice(-MAX_RECORDED_IDS),
    ),
  );
}

/**
 * Shows a toast for the claimed notifications and records them once it showed; otherwise releases
 * them so the next catch-up tries again.
 */
async function showClaimed(
  userId: string,
  ids: string[],
  toast: ToastContent,
  show: ShowToast,
): Promise<void> {
  let shown = false;
  try {
    shown = await show(toast);
  } finally {
    if (shown) {
      recordShown(userId, ids);
    } else {
      ids.forEach((id) => claimedIds.delete(id));
    }
  }
}

/**
 * Shows a notification the channel delivered, unless this window already showed it from a catch-up.
 */
export async function showNotificationOnce(
  userId: string,
  notification: AppNotification,
  show: ShowToast,
): Promise<void> {
  // A server build from before the notification log sends no id, so there is nothing to claim or
  // record.
  if (!notification.id) {
    await show(notification);
    return;
  }
  if (claimedIds.has(notification.id)) {
    return;
  }
  claimedIds.add(notification.id);
  await showClaimed(userId, [notification.id], notification, show);
}

/**
 * Shows notifications the desktop app missed, oldest first, or one toast that sums them up when
 * there are more than three. On a first run it records the log without showing anything old.
 * It stops once `signal` aborts, such as when the person signs out.
 */
export async function catchUpMissedNotifications(
  userId: string,
  show: ShowToast,
  signal: AbortSignal,
): Promise<void> {
  const shownIds = readShownIds(userId);
  const response = await fetch('/api/desktop/notifications', { signal });
  if (!response.ok) {
    throw new Error(`Failed to load missed notifications (${response.status})`);
  }
  const { notifications } = (await response.json()) as { notifications: AppNotification[] };
  if (signal.aborted) {
    return;
  }
  if (!shownIds) {
    recordShown(
      userId,
      notifications.map(({ id }) => id),
    );
    return;
  }
  const missed = notifications.filter(({ id }) => !shownIds.includes(id) && !claimedIds.has(id));
  missed.forEach(({ id }) => claimedIds.add(id));
  const toasts =
    missed.length > MAX_CATCH_UP_TOASTS
      ? [
          {
            ids: missed.map(({ id }) => id),
            toast: {
              title: `${missed.length} updates while you were away`,
              body: '',
              link: `${CANONICAL_INBOX_PATH}#notifications`,
              quickAction: null,
              desktopOnly: true,
            },
          },
        ]
      : missed.map((notification) => ({ ids: [notification.id], toast: notification }));
  for (const [index, { ids, toast }] of toasts.entries()) {
    if (signal.aborted) {
      for (const remaining of toasts.slice(index)) {
        remaining.ids.forEach((id) => claimedIds.delete(id));
      }
      return;
    }
    await showClaimed(userId, ids, toast, show);
  }
}
